import { useGetBlobContractorsQuery, usePackage, useProjectId } from '@consigli/hooks';
import { useMemo } from 'react';
import { useParams } from 'react-router';

// Keys matching the backend (breeze) and the bid-review wizard payload shape.
const BIDSLIST_KEY = 'bidslist';
const OTHER_BIDS_KEY = 'other_bids';

interface OtherBidEntry {
  provider_name?: string;
}

/**
 * Resolve the list of bid contractors for a Bid Review package.
 *
 * The canonical source is the package `data` written by the bid-review wizard:
 * direct bids in `bidslist` and cross-package bids in `other_bids`. When both
 * are absent (legacy packages that predate the wizard) the hook falls back to
 * `blobs/contractors/`, which derives names from blob `reference` metadata.
 */
export const useBidReviewContractors = (
  options: { skip?: boolean } = {},
): { contractors: string[]; isLoading: boolean } => {
  const projectId = useProjectId();
  const packageId = useParams<{ packageId: string }>().packageId ?? '';
  const { pkg, isLoading: packageLoading } = usePackage(projectId, packageId);

  const dataContractors = useMemo(() => {
    const data = pkg?.data ?? {};
    const direct: string[] = Array.isArray(data[BIDSLIST_KEY])
      ? (data[BIDSLIST_KEY] as string[]).filter(Boolean)
      : [];
    const other: string[] = Array.isArray(data[OTHER_BIDS_KEY])
      ? (data[OTHER_BIDS_KEY] as OtherBidEntry[])
          .map((entry) =>
            typeof entry?.provider_name === 'string' ? entry.provider_name.trim() : '',
          )
          .filter(Boolean)
      : [];
    return [...new Set([...direct, ...other])];
  }, [pkg?.data]);

  const hasDataContractors = dataContractors.length > 0;

  // Only query the blob endpoint when the package data has no contractor names
  // (legacy packages). The skip avoids a redundant network round-trip when the
  // data already tells us the answer.
  const { data: blobContractors = [], isLoading: blobsLoading } = useGetBlobContractorsQuery(
    { projectId, packageId },
    { skip: options.skip || !packageId || hasDataContractors },
  );

  const contractors = hasDataContractors ? dataContractors : blobContractors;
  const isLoading =
    options.skip || !packageId ? false : packageLoading || (!hasDataContractors && blobsLoading);

  return { contractors, isLoading };
};
